import { useEffect, useId, useRef, type ReactNode } from 'react';
import type { AppPlan } from '@/webapp/plan/planTypes';
import { keepLiveTitle, type Offer } from '../../state/trial';
import type { PlanId } from '../../state/plans';
import { Button } from '../../ui';
import PlanChoice from './PlanChoice';

/** At most this many of the plan's features: enough to recognise the app, short enough for a phone. */
const FEATURES_SHOWN = 5;

/*
 * What comes with the plan, in the words of what the person will do with it. Each is a thing the
 * product does today for every app it builds: a published page at its own address
 * (app.saddle-sites.com/site/<name>), accounts and saved rows for the people who use it (the
 * runtime's S.auth and S.list), and changes made through the editor's chat. How many sites and
 * changes is the plan's, said on its card (state/plans.ts).
 */
const WHAT_YOU_GET = [
  'Your site, live at its own link',
  'Sign-ins and saved data for the people who use it',
  'Changes made just by chatting',
];

/**
 * The moment the card is asked for, for somebody without a plan.
 *
 * It was one line of terms over a button, and then Stripe: the person had walked the plan, but
 * the screen that asked for their card said nothing about what the card was for. This says it —
 * their app by name and what it will do, and the plans to choose from in the shared words
 * (state/plans.ts, so they cannot drift from Credits or the return from Stripe) — and one tap goes
 * on with the plan chosen. A subscriber never sees it: Build builds.
 */
export default function Paywall({
  plan,
  offer,
  busy,
  continueLabel,
  notice,
  ready = false,
  building,
  giftEnded = null,
  backLabel = 'Back to your plan',
  freeWeekUntil,
  trialEndsAt,
  onContinue,
  onBack,
}: {
  plan: AppPlan;
  offer: Offer;
  busy: boolean;
  /** The button's words: the plan's, or "Continue to checkout" once a warning has been said. */
  continueLabel?: string;
  /** Said above the button: why the last press stopped, or what checkout cannot carry. */
  notice?: ReactNode;
  /**
   * The app is built and waiting on the server for the trial (state/buildRun.ts `heldApp`): said,
   * so the card finishes something that already exists. Its page is not shown until it is paid for.
   */
  ready?: boolean;
  /**
   * The app is being built behind this panel (2026-10-05), and where it has got to. Said plainly:
   * how long it takes, and that the trial can start now and opens it when it is done.
   */
  building?: string;
  /** A gift of free credits that has run out or ended: said first, calmly, with the ordinary offer. */
  giftEnded?: 'used_up' | 'expired' | null;
  /** The quiet way out's words: back to the plan, or — over their site — "Not now". */
  backLabel?: string;
  /**
   * V6: the app is live on its free week until this date (ISO). The offer is then to keep it live
   * after that: the plan's trial, its card first charged on this date. Past it, the app is paused
   * and the plan starts at once. Its own title either way.
   */
  freeWeekUntil?: string;
  /** ISO: when a trial taken now first charges, where that is not today + 14 days (a free week's end). */
  trialEndsAt?: string;
  onContinue: (plan: PlanId) => void;
  onBack: () => void;
}) {
  const headingId = useId();
  const doesId = useId();
  const getsId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const keepLive = freeWeekUntil ? keepLiveTitle(plan.appName, freeWeekUntil) : null;
  /*
   * A free week still running is a trial ending on its last day (the server gives it the days left);
   * one that has ended is the plan from today.
   */
  const weekRunning = freeWeekUntil !== undefined && Date.parse(freeWeekUntil) > Date.now();
  const shownOffer: Offer = freeWeekUntil !== undefined ? (weekRunning ? 'trial' : 'subscribe') : offer;
  const chargeAt = weekRunning ? freeWeekUntil : trialEndsAt;
  const features = plan.features.filter((f) => f.included).slice(0, FEATURES_SHOWN);

  // The press that opened this was at the bottom of a long step; its top is what they need to see.
  useEffect(() => {
    heading.current?.focus();
  }, []);

  return (
    <section className="paywall" aria-labelledby={headingId}>
      <h2 id={headingId} ref={heading} tabIndex={-1} className="type-title paywall-name">{keepLive ?? plan.appName}</h2>
      {giftEnded && (
        <p className="type-body paywall-gift" role="status">
          {giftEnded === 'expired' ? 'Your free credits have ended.' : 'Your free credits are used up.'} Choose a plan to keep going.
        </p>
      )}
      {keepLive ? null : ready ? (
        <p className="type-body paywall-ready" role="status">Your {plan.appName} is ready to go live.</p>
      ) : building ? (
        <div className="paywall-building" role="status">
          <p className="type-body paywall-ready">
            Your {plan.appName} is building. {shownOffer === 'trial' ? 'Start your free trial' : 'Choose a plan'} to open it the moment it’s ready.
          </p>
          <p className="type-caption paywall-building-step">
            <span className="paywall-building-dot" aria-hidden="true" /> {building} It takes about 4 minutes.
          </p>
        </div>
      ) : (
        plan.tagline && <p className="type-body plan-detail">{plan.tagline}</p>
      )}

      <PlanChoice
        offer={shownOffer}
        trialEndsAt={chargeAt}
        busy={busy}
        appName={plan.appName}
        continueLabel={continueLabel}
        notice={notice}
        accessibilityLabel={keepLive ? undefined : (label) => `${label} and build this app`}
        onChoose={onContinue}
      />

      {features.length > 0 && (
        <>
          <h3 id={doesId} className="type-micro section-label">What it does</h3>
          <ul className="plan-review-list" aria-labelledby={doesId}>
            {features.map((f) => (
              <li key={f.id} className="type-body">{f.label}</li>
            ))}
          </ul>
        </>
      )}

      <h3 id={getsId} className="type-micro section-label">What you get</h3>
      <ul className="paywall-gets" aria-labelledby={getsId}>
        {WHAT_YOU_GET.map((line) => (
          <li key={line} className="type-body">
            <span className="paywall-tick" aria-hidden="true">✓</span>
            {line}
          </li>
        ))}
      </ul>

      <div className="paywall-actions">
        <Button title={backLabel} variant="ghost" onPress={onBack} disabled={busy} />
      </div>
    </section>
  );
}
